import { Injectable } from '@angular/core';
import { CompaniesService } from './companies.service';

export interface RoleDefinition {
  level: number;
  permissions: string[];
  description: string;
  isStandalone: boolean;
}

export interface RoleDefinitions {
  [key: string]: RoleDefinition;
}

@Injectable({
  providedIn: 'root'
})
export class RolePermissionsService {
  
  // Role definitions from backend permissions.js
  private readonly ROLE_DEFINITIONS: RoleDefinitions = {
    // Consolidated hierarchy roles
    'Organization Admin': {
      level: 100,
      permissions: ['read', 'write', 'manage_users', 'manage_companies', 'manage_billing', 'delete', 'manage_sequences', 'view_analytics', 'export_data'],
      description: 'Full access to parent organization and all child accounts',
      isStandalone: false
    },
    'Organization Manager': {
      level: 90,
      permissions: ['read', 'write', 'manage_users', 'manage_sequences', 'view_analytics'],
      description: 'Cross-company admin access without billing or delete permissions',
      isStandalone: false
    },
    'Sub-Account Admin': {
      level: 80,
      permissions: ['read', 'write', 'manage_users', 'manage_sequences', 'view_analytics'],
      description: 'Administrative access to child account',
      isStandalone: false
    },

    // Standalone company roles
    'Company Owner': {
      level: 95,
      permissions: ['read', 'write', 'manage_users', 'manage_billing', 'delete', 'manage_sequences', 'view_analytics', 'export_data'],
      description: 'Full ownership of standalone company',
      isStandalone: true
    },
    'Admin': {
      level: 85,
      permissions: ['read', 'write', 'manage_users', 'manage_sequences', 'view_analytics'],
      description: 'Administrative access to standalone company (no billing/delete)',
      isStandalone: true
    },
    'Manager': {
      level: 60,
      permissions: ['read', 'write', 'manage_sequences', 'view_analytics'],
      description: 'Can manage sequences and view analytics',
      isStandalone: true
    },
    'Editor': {
      level: 40,
      permissions: ['read', 'write', 'manage_sequences'],
      description: 'Can create and edit sequences',
      isStandalone: true
    },
    'Viewer': {
      level: 20,
      permissions: ['read', 'view_analytics'],
      description: 'Read-only access with analytics',
      isStandalone: true
    },
    'User': {
      level: 10,
      permissions: ['read'],
      description: 'Basic read-only access',
      isStandalone: true
    }
  };

  constructor(private companiesService: CompaniesService) {}

  /**
   * Get role definition for a specific role
   */
  getRoleDefinition(role: string): RoleDefinition | null {
    return this.ROLE_DEFINITIONS[role] || null;
  }

  /**
   * Check if user has specific permission based on their role
   */
  hasPermission(userRole: string, requiredPermission: string): boolean {
    const roleDefinition = this.getRoleDefinition(userRole);
    return roleDefinition ? roleDefinition.permissions.includes(requiredPermission) : false;
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(userRole: string, requiredPermissions: string[]): boolean {
    const roleDefinition = this.getRoleDefinition(userRole);
    if (!roleDefinition) return false;
    
    return requiredPermissions.some(permission => 
      roleDefinition.permissions.includes(permission)
    );
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(userRole: string, requiredPermissions: string[]): boolean {
    const roleDefinition = this.getRoleDefinition(userRole);
    if (!roleDefinition) return false;
    
    return requiredPermissions.every(permission => 
      roleDefinition.permissions.includes(permission)
    );
  }

  /**
   * Check if user can perform action based on role level
   */
  canPerformAction(userRole: string, targetRole: string, action: 'manage' | 'assign_role' | 'remove' | 'view'): boolean {
    const userLevel = this.ROLE_DEFINITIONS[userRole]?.level || 0;
    const targetLevel = this.ROLE_DEFINITIONS[targetRole]?.level || 0;

    switch (action) {
      case 'manage':
      case 'assign_role':
      case 'remove':
        return userLevel > targetLevel;
      case 'view':
        return userLevel >= targetLevel;
      default:
        return false;
    }
  }

  /**
   * Get current user's role from selected company
   */
  getCurrentUserRole(): string | null {
    const selectedCompany = this.companiesService.getSelectedCompany();
    return selectedCompany?.userRole || null;
  }

  /**
   * Get current user's permissions based on their role in the selected company
   */
  getCurrentUserPermissions(): string[] {
    const currentRole = this.getCurrentUserRole();
    if (!currentRole) return [];
    
    const roleDefinition = this.getRoleDefinition(currentRole);
    return roleDefinition ? roleDefinition.permissions : [];
  }

  /**
   * Check if current user has specific permission
   */
  currentUserHasPermission(requiredPermission: string): boolean {
    const currentRole = this.getCurrentUserRole();
    return currentRole ? this.hasPermission(currentRole, requiredPermission) : false;
  }

  /**
   * Check if current user has any of the specified permissions
   */
  currentUserHasAnyPermission(requiredPermissions: string[]): boolean {
    const currentRole = this.getCurrentUserRole();
    return currentRole ? this.hasAnyPermission(currentRole, requiredPermissions) : false;
  }

  /**
   * Check if current user has all of the specified permissions
   */
  currentUserHasAllPermissions(requiredPermissions: string[]): boolean {
    const currentRole = this.getCurrentUserRole();
    return currentRole ? this.hasAllPermissions(currentRole, requiredPermissions) : false;
  }

  /**
   * Check if current user is viewer role (read-only)
   */
  isCurrentUserViewer(): boolean {
    const currentRole = this.getCurrentUserRole();
    return currentRole === 'Viewer';
  }

  /**
   * Check if current user can create/edit content (has write permission)
   */
  canCurrentUserWrite(): boolean {
    return this.currentUserHasPermission('write');
  }

  /**
   * Check if current user can manage users
   */
  canCurrentUserManageUsers(): boolean {
    return this.currentUserHasPermission('manage_users');
  }

  /**
   * Check if current user can manage companies
   */
  canCurrentUserManageCompanies(): boolean {
    // Check for explicit manage_companies permission
    if (this.currentUserHasPermission('manage_companies')) {
      return true;
    }
    
    // Also allow Manager role and above (level 60+) to manage companies
    // Manager level is 60, so any role at that level or higher can manage companies
    const currentRole = this.getCurrentUserRole();
    if (!currentRole) return false;
    
    const roleDefinition = this.getRoleDefinition(currentRole);
    return roleDefinition ? roleDefinition.level >= 60 : false;
  }

  /**
   * Check if current user can manage sequences
   */
  canCurrentUserManageSequences(): boolean {
    return this.currentUserHasPermission('manage_sequences');
  }

  /**
   * Check if current user has elevated access (admin level or above)
   */
  hasCurrentUserElevatedAccess(): boolean {
    const currentRole = this.getCurrentUserRole();
    if (!currentRole) return false;
    
    const roleDefinition = this.getRoleDefinition(currentRole);
    return roleDefinition ? roleDefinition.level >= 70 : false; // Admin level and above
  }

  /**
   * Get roles that current user can assign (lower level than their own)
   */
  getAssignableRoles(): RoleDefinitions {
    const currentRole = this.getCurrentUserRole();
    if (!currentRole) return {};
    
    const userLevel = this.ROLE_DEFINITIONS[currentRole]?.level || 0;

    return Object.entries(this.ROLE_DEFINITIONS)
      .filter(([_, definition]) => definition.level <= userLevel)
      .reduce((acc, [role, definition]) => {
        acc[role] = definition;
        return acc;
      }, {} as RoleDefinitions);
  }

}